import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/helpers";
import {
  trackSearch,
  trackAnalysisView,
  trackSaveProperty,
  trackOnboardingComplete,
} from "../lib/analytics";
import { trackPropertyClick, trackSearch as trackSearchEvent } from "../lib/tracking";
import { useAnalysisSession } from "../hooks/useAnalysisSession";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Search, Heart, Bell, BarChart3, Building2, Lock, Briefcase, SlidersHorizontal, RotateCcw, Pencil, Check, Upload } from "lucide-react";

import EliteUpsellBanner from "../components/EliteUpsellBanner";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import SearchTab from "../components/dashboard/SearchTab";
import SavedTab from "../components/dashboard/SavedTab";
import AlertsTab from "../components/dashboard/AlertsTab";
import MarketCompareTab from "../components/dashboard/MarketCompareTab";
import CommercialSearchTab from "../components/dashboard/CommercialSearchTab";  // eslint-disable-line no-unused-vars -- temporarily removed from UI; kept for future re-enable
import PortfolioTab from "../components/dashboard/PortfolioTab";
import BulkAnalyzeTab from "../components/dashboard/BulkAnalyzeTab";
import { MarketAlertsWidget, DealOfDayWidget } from "../components/dashboard/DashboardWidgets";

import AnalysisModal from "../components/AnalysisModal";
import ComparisonModal from "../components/ComparisonModal";
import OnboardingModal from "../components/OnboardingModal";
import MarketSelectionModal from "../components/MarketSelectionModal";
import FeedbackWidget from "../components/FeedbackWidget";
import AnalysisDefaultsEditor from "../components/AnalysisDefaultsEditor";
import SupportContact from "../components/SupportContact";
import UpgradeModal from "../components/UpgradeModal";
import CreateAlertModal from "../components/CreateAlertModal";
import Footer from "../components/Footer";

export default function DashboardPage({ user, logout, isAuthenticated }) {
  const navigate = useNavigate();
  const { registerAnalysis, trackFunnelEvent } = useAnalysisSession();
  const [activeTab, setActiveTab] = useState("search");
  const [properties, setProperties] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [analyses, setAnalyses] = useState({});
  const [savedPropertyIds, setSavedPropertyIds] = useState(new Set());
  const [savedProperties, setSavedProperties] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateAlertOpen, setIsCreateAlertOpen] = useState(false);
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState("properties");
  const [subscription, setSubscription] = useState(null);
  const [propertiesVisible, setPropertiesVisible] = useState(-1);
  const [analysisParams, setAnalysisParams] = useState({
    down_payment_percent: 20,
    closing_costs_percent: 3,
    repair_costs: 5000,
    loan_term_years: 30,
    interest_rate: 6.5,
    capex_percent: 0,
    loan_points: 0,
    electricity: 0,
    gas: 0,
    water_sewer: 0,
    garbage: 0,
    other_expenses: 0,
    // Flip params
    flip_rehab_scope: "cosmetic",
    flip_rehab_per_sqft: 0,
    flip_financing_rate: 12.0,
    flip_financing_points: 2.0,
    flip_financing_ltv: 85.0,
    flip_agent_commission: 5.0,
    flip_monthly_utilities: 200,
  });
  const [hasCustomDefaults, setHasCustomDefaults] = useState(false);
  const [showDefaultsEditor, setShowDefaultsEditor] = useState(false);
  const hasCustomDefaultsRef = useRef(false);
  const [currentMortgageRate, setCurrentMortgageRate] = useState(null);
  const [filters, setFilters] = useState({
    location: "",
    min_price: null,
    max_price: null,
    min_beds: null,
    min_baths: null,
    property_types: null,
  });
  const [smartSort, setSmartSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);  // local pagination within current batch
  const [apiPage, setApiPage] = useState(1);  // Zillow API page
  const debounceRef = useRef(null);
  const isInitialMount = useRef(true);
  // ---- Search cancellation + in-session caches (SEC/PERF fix Feb 2026) ----
  // Every filter change previously fired a fresh `/api/properties` + 41
  // `/api/properties/{id}/analysis` calls WITHOUT cancelling the prior
  // batch. Rapid clicks piled up 100+ pending requests and appeared
  // frozen. Now:
  //   * `searchAbortRef` — active AbortController, cancelled on next call
  //   * `searchCacheRef` — Map<queryString, {props, total, visible, ts}>
  //     so toggling filters back and forth reuses a still-fresh result
  //   * `analysisCacheRef` — Map<`${propertyId}|${analysisParamsKey}`,
  //     analysis> so repeated searches within the same session don't
  //     re-fetch analyses the server has already computed.
  const searchAbortRef = useRef(null);
  const searchCacheRef = useRef(new Map());
  const analysisCacheRef = useRef(new Map());
  const SEARCH_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  const [investmentFilters, setInvestmentFilters] = useState({
    onlyPositiveCashFlow: false,
    only1PercentRule: false,
    only50PercentRule: false,
    maxHoa: null,
    minCapRate: null,
    minRent: null,
    minCashFlow: null,
    minDealScore: null,
    onlyBRRRR: false,
    showFlipData: false,
    showAirbnbData: false,
    // Flipper Buy Box
    maoRule: '75',
    maoCustomPct: null,
    onlyPassesMAO: false,
    minFlipProfit: null,
    minFlipROI: null,
    minARV: null,
    maxARV: null,
    maxRehabLevel: 'any',
    noFloodZone: false,
  });
  const [sortBy, setSortBy] = useState("default");
  const [strategyMode, setStrategyMode] = useState(() => {
    try { return localStorage.getItem('realix_strategy') || 'rental'; } catch { return 'rental'; }
  });
  // Custom Deal Scoring (Elite) — id + weights. Persisted per-user in
  // localStorage so the strategy sticks across page reloads. The actual
  // weights are computed client-side (see lib/dealScoring.js) which
  // mirrors the Python engine so exports/emails stay consistent.
  const [activeStrategyId, setActiveStrategyId] = useState(() => {
    try { return localStorage.getItem('realix_deal_strategy') || 'balanced'; } catch { return 'balanced'; }
  });
  const [activeStrategyWeights, setActiveStrategyWeights] = useState(null);
  const [activeStrategyCriteria, setActiveStrategyCriteria] = useState(() => {
    try {
      const raw = localStorage.getItem('realix_deal_criteria');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const handleActiveStrategyChange = useCallback((id, weights, criteria = null) => {
    setActiveStrategyId(id);
    setActiveStrategyWeights(weights || null);
    setActiveStrategyCriteria(criteria || null);
    try {
      localStorage.setItem('realix_deal_strategy', id);
      if (weights) localStorage.setItem('realix_deal_weights', JSON.stringify(weights));
      if (criteria) localStorage.setItem('realix_deal_criteria', JSON.stringify(criteria));
      else localStorage.removeItem('realix_deal_criteria');
    } catch (err) {
      console.debug('[strategy] localStorage write failed:', err);
    }
    // Fire-and-forget instrumentation event.
    axios.post(`${API}/analytics/track`, { event_type: 'strategy_applied', metadata: { strategy_id: id } })
      .catch((err) => console.debug('[strategy] track failed:', err));
  }, []);

  // Hydrate weights on mount from localStorage, then reconcile with the
  // canonical /api/strategies list to catch renamed/deleted customs.
  useEffect(() => {
    // Read the id fresh from localStorage so we don't depend on the
    // React state closure (which would force this effect to re-run and
    // clobber user picks).
    let mountedId = 'balanced';
    try {
      mountedId = localStorage.getItem('realix_deal_strategy') || 'balanced';
      const cached = localStorage.getItem('realix_deal_weights');
      if (cached) setActiveStrategyWeights(JSON.parse(cached));
    } catch (err) {
      console.debug('[strategy] localStorage read failed:', err);
    }
    axios.get(`${API}/strategies`).then(r => {
      const preset = (r.data.presets || []).find(p => p.id === mountedId);
      if (preset) {
        setActiveStrategyWeights(preset.weights);
        setActiveStrategyCriteria(null);
        return;
      }
      if (mountedId?.startsWith('custom_')) {
        const cid = mountedId.replace('custom_', '');
        const c = (r.data.custom || []).find(x => x.strategy_id === cid);
        if (c) {
          setActiveStrategyWeights(c.weights);
          setActiveStrategyCriteria(c.criteria || null);
        }
        else {
          // Custom strategy no longer exists — fall back to balanced.
          setActiveStrategyId('balanced');
          setActiveStrategyCriteria(null);
          const bal = (r.data.presets || []).find(p => p.id === 'balanced');
          if (bal) setActiveStrategyWeights(bal.weights);
        }
      }
    }).catch((err) => console.debug('[strategy] hydrate failed:', err));
  }, []);
  const [listingStatuses, setListingStatuses] = useState({
    for_sale: false,
    recently_sold: false,
    seized: false,
    // Feb 2026 — Zillow-supported statuses only. Coverage probe showed
    // the wrapper's /v1/search ignores ComingSoon/Foreclosed/NewBuild
    // status values, so those chips were removed rather than silently
    // returning empty. `price_reduced` + `open_house` are post-filters
    // applied server-side after the fetch.
    for_rent: false,
    price_reduced: false,
    open_house: false,
  });
  const [multiSubTypes, setMultiSubTypes] = useState({
    duplex: false,
    triplex: false,
    quadplex: false,
  });
  const [theme, setTheme] = useState(
    () => localStorage.getItem("relixdata-theme") || "dark"
  );
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const handleStrategyChange = (mode) => {
    setStrategyMode(mode);
    try { localStorage.setItem('realix_strategy', mode); } catch { /* best-effort preference */ }
    setInvestmentFilters(prev => ({ ...prev, showFlipData: mode === 'flip' }));
  };
  const [isComparisonOpen, setIsComparisonOpen] = useState(false);
  const [comparisonItems, setComparisonItems] = useState([]);
  // Check if there's a property param - if so, delay onboarding until after teaser check
  const hasPropertyParam = new URLSearchParams(window.location.search).has("property");
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem("relixdata_onboarded") && !hasPropertyParam
  );
  const [savedSearches, setSavedSearches] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [showMarketModal, setShowMarketModal] = useState(false);
  const [userMarkets, setUserMarkets] = useState(null); // null = not loaded yet
  const subscriptionRef = useRef(null);
  const [isAuthGateOpen, setIsAuthGateOpen] = useState(false);

  // IMPORTANT: searchProperties must be defined BEFORE useEffects that reference it
  // to avoid "Cannot access 'searchProperties' before initialization" error
  const searchProperties = useCallback(
    async (page = 1) => {
      const pageNum = typeof page === "number" ? page : 1;
      if (pageNum === 1) setApiPage(1);

      // 1. Cancel any in-flight search + its analysis fan-out. Every new
      //    Search click supersedes the previous one — no request pile-up.
      if (searchAbortRef.current) {
        try { searchAbortRef.current.abort(); } catch { /* prior request already settled */ }
      }
      const controller = new AbortController();
      searchAbortRef.current = controller;

      setLoading(true);
      setHasSearched(true);
      trackSearch(filters.location);
      try {
        const params = new URLSearchParams();
        if (filters.location) params.append("location", filters.location);
        if (filters.min_price) params.append("min_price", filters.min_price);
        if (filters.max_price) params.append("max_price", filters.max_price);
        if (filters.min_beds) params.append("min_beds", filters.min_beds);
        if (filters.min_baths) params.append("min_baths", filters.min_baths);
        if (filters.property_types)
          params.append("property_types", filters.property_types);
        const activeStatuses = Object.entries(listingStatuses)
          .filter(([k, v]) => v && k !== "seized")
          .map(([k]) => k);
        if (activeStatuses.length > 0)
          params.append("status_types", activeStatuses.join(","));
        if (filters.listed_within_days)
          params.append("listed_within_days", filters.listed_within_days);
        if (smartSort) params.append("smart_sort", "true");
        params.append("page", pageNum.toString());

        let propList = [];
        let totalCountVal = 0;
        let propertiesVisibleVal = -1;

        // 2. Client-side search cache. Same filter combo + page within
        //    5 min → skip the network round-trip entirely.
        const cacheKey = params.toString();
        const cached = searchCacheRef.current.get(cacheKey);
        if (cached && Date.now() - cached.ts < SEARCH_CACHE_TTL_MS) {
          propList = cached.props;
          totalCountVal = cached.total;
          propertiesVisibleVal = cached.visible;
        } else {
          const response = await axios.get(
            `${API}/properties?${params.toString()}`,
            { signal: controller.signal, timeout: 45000 }
          );
          propList = response.data.properties || [];
          totalCountVal = response.data.total_count || 0;
          propertiesVisibleVal = response.data.properties_visible ?? -1;
          searchCacheRef.current.set(cacheKey, {
            props: propList,
            total: totalCountVal,
            visible: propertiesVisibleVal,
            ts: Date.now(),
          });
        }

        if (listingStatuses.seized) {
          try {
            const seizedResp = await axios.get(`${API}/seized-properties`);
            const seizedProps = (seizedResp.data.properties || []).map(
              (sp) => ({
                id: `seized_${sp.sale_number || sp.full_address}`,
                address: sp.address || sp.full_address,
                city: sp.city || "",
                state: sp.state || "",
                zip_code: sp.zip_code || "",
                price: sp.starting_bid || 0,
                bedrooms: sp.bedrooms || 0,
                bathrooms: sp.bathrooms || 0,
                sqft: sp.sqft || 0,
                property_type: sp.property_type || "single_family",
                year_built: sp.year_built || 0,
                image_url:
                  sp.image_url ||
                  (sp.image_urls && sp.image_urls[0]) ||
                  "",
                listing_url: sp.detail_url || "",
                listing_source: "US Treasury",
                days_on_market: 0,
                units: 1,
                listing_status: "seized",
                is_seized: true,
                auction_date: sp.auction_date || sp.auction_datetime,
                starting_bid: sp.starting_bid,
                deposit: sp.deposit,
                sale_number: sp.sale_number,
                sale_type: sp.sale_type,
                description: sp.description,
                flyer_url: sp.flyer_url,
                floor_plan_url: sp.floor_plan_url,
                gallery_url: sp.gallery_url,
                tour_url: sp.tour_url,
                lot_size: sp.lot_size,
                county: sp.county,
                zoning: sp.zoning,
                utilities: sp.utilities,
                taxes_annual: sp.taxes_annual,
                inspections: sp.inspections,
              })
            );
            propList = [...propList, ...seizedProps];
            totalCountVal += seizedResp.data.total_count || 0;
          } catch (e) {
            console.error("Error fetching seized properties:", e);
          }
        }

        // Append results for page > 1, replace for page 1
        if (pageNum > 1) {
          setProperties(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            const newProps = propList.filter(p => !existingIds.has(p.id));
            return [...prev, ...newProps];
          });
        } else {
          setProperties(propList);
        }
        setTotalCount(totalCountVal);
        setCurrentPage(1);
        setPropertiesVisible(propertiesVisibleVal);
        setLoading(false);
        toast.success(
          `Found ${propList.length} properties (${totalCountVal.toLocaleString()} total)`
        );

        // Conversion telemetry — if the user picked a suggestion within
        // the last 30 seconds, this search counts as the "after-select
        // analysis". Logs success vs empty so the admin pipeline widget
        // can compute select→analysis conversion %.
        try {
          const lastSel = typeof window !== "undefined" ? window.__realixLastAutocompleteSelect : null;
          if (lastSel && Date.now() - lastSel < 30_000) {
            const event = propList.length > 0
              ? "geocoding_analysis_success"
              : "geocoding_analysis_empty";
            const payload = {
              event,
              result_count: propList.length,
              duration_ms: Date.now() - lastSel,
              current_path: window.location.pathname,
            };
            const url = `${API}/analytics/geocoding-event`;
            const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
            if (!(navigator.sendBeacon && navigator.sendBeacon(url, blob))) {
              fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload), keepalive: true }).catch(() => {});
            }
            // Clear so a subsequent paginate / re-search doesn't double-count
            window.__realixLastAutocompleteSelect = null;
          }
        } catch (_) { /* best-effort analytics beacon */ }

        axios
          .get(`${API}/subscription`)
          .then((r) => setSubscription(r.data))
          .catch(() => {});

        const fetchAnalyses = async () => {
          // For free users, don't auto-analyze — save their weekly limit
          if (!subscriptionRef.current?.is_paid) return;

          const batchSize = 5;
          const analysisMap = {};
          let slowToastShown = false;
          const startTime = Date.now();
          // Stable key for the current analysis-params combo so cache
          // lookups only reuse results computed with the SAME defaults.
          const paramsKey = JSON.stringify(analysisParams);

          for (let i = 0; i < propList.length; i += batchSize) {
            // Bail immediately if a newer search superseded us.
            if (controller.signal.aborted) return;
            // Show slow-loading message after 10 seconds
            if (!slowToastShown && Date.now() - startTime > 10000) {
              slowToastShown = true;
              toast.info("Analysis is taking longer than usual — fetching fresh market data for accurate results.", { duration: 8000 });
            }
            const batch = propList.slice(i, i + batchSize);
            const batchResults = await Promise.all(
              batch.map(async (prop) => {
                if (prop.is_seized)
                  return { id: prop.id, analysis: null };
                // In-session cache — skip the network entirely on
                // repeat searches of properties we already analysed
                // with the same params.
                const cacheKey = `${prop.id}|${paramsKey}`;
                const cachedAnalysis = analysisCacheRef.current.get(cacheKey);
                if (cachedAnalysis) {
                  return { id: prop.id, analysis: cachedAnalysis };
                }
                try {
                  const analysisRes = await axios.get(
                    `${API}/properties/${prop.id}/analysis`,
                    {
                      params: analysisParams,
                      signal: controller.signal,
                      // 20s hard timeout so one slow vendor call can't
                      // freeze the entire batch UI.
                      timeout: 20000,
                    }
                  );
                  analysisCacheRef.current.set(cacheKey, analysisRes.data);
                  return { id: prop.id, analysis: analysisRes.data };
                } catch {
                  return { id: prop.id, analysis: null };
                }
              })
            );
            if (controller.signal.aborted) return;
            batchResults.forEach(({ id, analysis }) => {
              if (analysis) analysisMap[id] = analysis;
            });
            setAnalyses((prev) => ({ ...prev, ...analysisMap }));
          }
        };
        fetchAnalyses();
      } catch (error) {
        // Cancelled searches must never surface as errors — they're
        // just the previous search getting superseded by a newer one.
        if (axios.isCancel?.(error) || error.name === "CanceledError" || error.name === "AbortError") {
          return;
        }
        console.error("Error:", error);
        setLoading(false);
        const status = error.response?.status;
        const detail = error.response?.data?.detail;
        if ((status === 401 || status === 429) && detail?.anonymous_limit) {
          // Anonymous user hit their free search/analysis limit
          setIsAuthGateOpen(true);
        } else if (status === 401) {
          // Generic auth required — show auth gate
          setIsAuthGateOpen(true);
        } else if (status === 429 && detail?.upgrade_required) {
          setUpgradeReason("search");
          setIsUpgradeOpen(true);
        } else if (error.code === "ECONNABORTED" || /timeout/i.test(error.message || "")) {
          toast.error("Search timed out — the market you searched is slow. Try a narrower area or try again.");
        } else {
          toast.error(
            status === 429
              ? "API rate limit reached. Please try again in a few minutes."
              : "Failed to search"
          );
        }
      }
    },
    [filters, analysisParams, smartSort, listingStatuses, isAuthenticated] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("relixdata-theme", theme);
  }, [theme]);

  useEffect(() => {
    axios
      .get(`${API}/settings`)
      .then((r) => {
        if (r.data.theme) setTheme(r.data.theme);
        if (r.data.weekly_digest !== undefined)
          setWeeklyDigest(r.data.weekly_digest);
      })
      .catch(() => {});
  }, []);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    axios.patch(`${API}/settings`, { theme: next }).catch(() => {});
  };

  const toggleDigest = () => {
    const next = !weeklyDigest;
    setWeeklyDigest(next);
    axios.patch(`${API}/settings`, { weekly_digest: next }).catch(() => {});
    toast.success(
      next
        ? "Weekly digest enabled! You'll receive emails every Monday."
        : "Weekly digest disabled."
    );
  };

  const ITEMS_PER_PAGE = 24;

  const filteredProperties = useMemo(() => {
    const {
      onlyPositiveCashFlow,
      only1PercentRule,
      only50PercentRule,
      minCapRate,
      minRent,
      minCashFlow,
      minDealScore,
      onlyBRRRR,
      onlyPassesMAO,
      maoRule,
      maoCustomPct,
      minFlipProfit,
      minFlipROI,
      minARV,
      maxARV,
      maxRehabLevel,
      noFloodZone,
    } = investmentFilters;
    const anyInvestmentFilter =
      onlyPositiveCashFlow ||
      only1PercentRule ||
      only50PercentRule ||
      minCapRate ||
      minRent ||
      minCashFlow ||
      minDealScore ||
      onlyBRRRR;
    const anyFlipFilter =
      onlyPassesMAO || minFlipProfit || minFlipROI || minARV || maxARV ||
      (maxRehabLevel && maxRehabLevel !== 'any') || noFloodZone;
    const anySubTypeFilter =
      multiSubTypes.duplex || multiSubTypes.triplex || multiSubTypes.quadplex;

    // Rehab tier ordering for max level filter
    const rehabOrder = { cosmetic: 1, moderate: 2, medium: 2, heavy: 3, gut: 4 };
    const maxRehabNum = rehabOrder[maxRehabLevel] || 99;

    // Compute effective MAO percentage
    const maoPct = maoRule === 'custom' ? ((maoCustomPct || 75) / 100) : (parseInt(maoRule || '75') / 100);

    // Step 1: Filter — remove non-matching properties
    let result = properties;
    if (anyInvestmentFilter || anyFlipFilter || anySubTypeFilter) {
      result = properties.filter((p) => {
        if (anySubTypeFilter && p.property_type === "multi_family") {
          const beds = p.bedrooms || 0;
          const matchesDuplex = multiSubTypes.duplex && beds >= 2 && beds <= 3;
          const matchesTriplex = multiSubTypes.triplex && beds >= 4 && beds <= 5;
          const matchesQuadplex = multiSubTypes.quadplex && beds >= 6;
          if (!matchesDuplex && !matchesTriplex && !matchesQuadplex) return false;
        }
        const a = analyses[p.id];
        // If no analysis yet, keep the property visible (don't filter out)
        if (!a) return true;
        // Rental filters
        if (onlyPositiveCashFlow && a.cash_flow_monthly < 0) return false;
        if (onlyPositiveCashFlow && investmentFilters.showFlipData && a.fix_flip && (a.fix_flip.net_profit ?? 0) < 0) return false;
        if (onlyPositiveCashFlow && investmentFilters.showAirbnbData && a.airbnb && a.airbnb.net_monthly_income < 0) return false;
        if (only1PercentRule && !a.one_percent_rule) return false;
        if (only50PercentRule && !a.fifty_percent_rule) return false;
        if (minCapRate && a.cap_rate < minCapRate) return false;
        if (minRent && a.estimated_monthly_rent < minRent) return false;
        if (minCashFlow && a.cash_flow_monthly < minCashFlow) return false;
        if (minDealScore && a.deal_score < minDealScore) return false;
        if (onlyBRRRR && (a.brrrr_score || 0) < 60) return false;
        // Flipper Buy Box filters
        const ff = a.fix_flip;
        if (anyFlipFilter && ff) {
          if (onlyPassesMAO) {
            const maoValue = ff.arv * maoPct - ff.rehab_total;
            if (ff.purchase_price > maoValue) return false;
          }
          if (minFlipProfit && (ff.net_profit ?? 0) < minFlipProfit) return false;
          if (minFlipROI && (ff.cash_roi ?? 0) < minFlipROI) return false;
          if (minARV && (ff.arv ?? 0) < minARV) return false;
          if (maxARV && (ff.arv ?? 0) > maxARV) return false;
          if (maxRehabLevel && maxRehabLevel !== 'any') {
            const tierNum = rehabOrder[ff.rehab_tier] || 99;
            if (tierNum > maxRehabNum) return false;
          }
          if (noFloodZone && a.fema_flood_zone && a.fema_flood_zone !== 'X' && a.fema_flood_zone !== 'None') return false;
        }
        if (anyFlipFilter && !ff) return false; // No flip data = skip for flip filters
        return true;
      });
    }

    // Step 2: Sort — always apply sort AFTER filtering
    const flipActive = investmentFilters.showFlipData;
    const airbnbActive = investmentFilters.showAirbnbData;

    if (sortBy === "deal_score") {
      result = [...result].sort((a, b) => {
        const aScore = flipActive ? (analyses[a.id]?.fix_flip?.deal_score ?? 0) : (analyses[a.id]?.deal_score ?? 0);
        const bScore = flipActive ? (analyses[b.id]?.fix_flip?.deal_score ?? 0) : (analyses[b.id]?.deal_score ?? 0);
        return bScore - aScore;
      });
    } else if (sortBy === "deal_score_low") {
      result = [...result].sort((a, b) => {
        const aScore = flipActive ? (analyses[a.id]?.fix_flip?.deal_score ?? 0) : (analyses[a.id]?.deal_score ?? 0);
        const bScore = flipActive ? (analyses[b.id]?.fix_flip?.deal_score ?? 0) : (analyses[b.id]?.deal_score ?? 0);
        return aScore - bScore;
      });
    } else if (sortBy === "brrrr_score") {
      result = [...result].sort((a, b) => (analyses[b.id]?.brrrr_score ?? 0) - (analyses[a.id]?.brrrr_score ?? 0));
    } else if (sortBy === "cash_flow") {
      result = [...result].sort((a, b) => {
        if (flipActive) return (analyses[b.id]?.fix_flip?.net_profit ?? -Infinity) - (analyses[a.id]?.fix_flip?.net_profit ?? -Infinity);
        if (airbnbActive) return (analyses[b.id]?.airbnb?.net_monthly_income ?? -Infinity) - (analyses[a.id]?.airbnb?.net_monthly_income ?? -Infinity);
        return (analyses[b.id]?.cash_flow_monthly ?? -Infinity) - (analyses[a.id]?.cash_flow_monthly ?? -Infinity);
      });
    } else if (sortBy === "cash_flow_low") {
      result = [...result].sort((a, b) => {
        if (flipActive) return (analyses[a.id]?.fix_flip?.net_profit ?? Infinity) - (analyses[b.id]?.fix_flip?.net_profit ?? Infinity);
        if (airbnbActive) return (analyses[a.id]?.airbnb?.net_monthly_income ?? Infinity) - (analyses[b.id]?.airbnb?.net_monthly_income ?? Infinity);
        return (analyses[a.id]?.cash_flow_monthly ?? Infinity) - (analyses[b.id]?.cash_flow_monthly ?? Infinity);
      });
    } else if (sortBy === "cap_rate") {
      result = [...result].sort((a, b) => (analyses[b.id]?.cap_rate ?? 0) - (analyses[a.id]?.cap_rate ?? 0));
    } else if (sortBy === "cap_rate_low") {
      result = [...result].sort((a, b) => (analyses[a.id]?.cap_rate ?? 0) - (analyses[b.id]?.cap_rate ?? 0));
    } else if (sortBy === "price_low") {
      result = [...result].sort((a, b) => (a.price || 0) - (b.price || 0));
    } else if (sortBy === "price_high") {
      result = [...result].sort((a, b) => (b.price || 0) - (a.price || 0));
    } else if (sortBy === "flip_profit") {
      result = [...result].sort((a, b) => (analyses[b.id]?.fix_flip?.net_profit ?? -Infinity) - (analyses[a.id]?.fix_flip?.net_profit ?? -Infinity));
    } else if (sortBy === "flip_profit_low") {
      result = [...result].sort((a, b) => (analyses[a.id]?.fix_flip?.net_profit ?? Infinity) - (analyses[b.id]?.fix_flip?.net_profit ?? Infinity));
    } else if (sortBy === "airbnb_income") {
      result = [...result].sort((a, b) => (analyses[b.id]?.airbnb?.net_monthly_income ?? -Infinity) - (analyses[a.id]?.airbnb?.net_monthly_income ?? -Infinity));
    } else if (sortBy === "airbnb_income_low") {
      result = [...result].sort((a, b) => (analyses[a.id]?.airbnb?.net_monthly_income ?? Infinity) - (analyses[b.id]?.airbnb?.net_monthly_income ?? Infinity));
    }

    return result;
  }, [properties, analyses, investmentFilters, multiSubTypes, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredProperties.length / ITEMS_PER_PAGE));
  const paginatedProperties = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredProperties.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProperties, currentPage]);

  // Reset to page 1 when filters or properties change
  useEffect(() => {
    setCurrentPage(1);
  }, [investmentFilters, multiSubTypes, sortBy, properties.length]);

  // Auto-load next API page when filtered results are too few.
  // Bounded to at most 2 auto-loads per manual search so a picky
  // filter combo can't chain-fire 5+ full page fetches (which was
  // the runaway root cause of the "not loading" complaints in prod).
  const autoLoadRef = useRef(false);
  const autoLoadCountRef = useRef(0);
  const lastManualPageRef = useRef(1);
  useEffect(() => {
    // Reset the auto-load counter whenever the user manually kicks
    // off a search from page 1.
    if (apiPage === 1 && lastManualPageRef.current !== 1) {
      autoLoadCountRef.current = 0;
    }
    lastManualPageRef.current = apiPage;
  }, [apiPage]);

  useEffect(() => {
    const anyFilterActive = investmentFilters.onlyPositiveCashFlow || investmentFilters.only1PercentRule ||
      investmentFilters.only50PercentRule || investmentFilters.minCapRate || investmentFilters.minDealScore ||
      investmentFilters.onlyPassesMAO || investmentFilters.minFlipProfit;

    // Only auto-load if: filters active, few results, more data
    // available, all current analyzed, not already loading, and we
    // haven't already auto-loaded twice for this manual search.
    const allAnalyzed = properties.length > 0 && properties.every(p => analyses[p.id] || p.is_seized);
    if (anyFilterActive && allAnalyzed && filteredProperties.length < 12 &&
        properties.length < totalCount && !loading && !autoLoadRef.current &&
        autoLoadCountRef.current < 2) {
      autoLoadRef.current = true;
      autoLoadCountRef.current += 1;
      searchProperties(apiPage + 1).finally(() => { autoLoadRef.current = false; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProperties.length, properties.length, analyses, investmentFilters, totalCount, loading, apiPage, searchProperties]);

  const analysisLoadProgress = useMemo(() => {
    const loaded = properties.filter((p) => analyses[p.id]).length;
    return {
      loaded,
      total: properties.length,
      loading: loaded < properties.length && properties.length > 0,
    };
  }, [properties, analyses]);

  // Keep subscriptionRef in sync
  useEffect(() => { subscriptionRef.current = subscription; }, [subscription]);

  // Fetch subscription and user markets, then decide whether to auto-search
  const initialAutoSearchDone = useRef(false);
  useEffect(() => {
    // Guard: only make auth-dependent calls if user is authenticated
    if (!user) return;

    // Fetch subscription
    axios.get(`${API}/subscription`).then((r) => {
      setSubscription(r.data);
      subscriptionRef.current = r.data;
    }).catch(() => {});

    // Fetch user's preferred markets
    axios.get(`${API}/user/markets`).then((r) => {
      const savedMarkets = r.data?.markets || [];
      setUserMarkets(savedMarkets);
      if (savedMarkets.length > 0) {
        // Set first market as the search location — auto-search will trigger via separate effect
        setFilters((f) => ({ ...f, location: savedMarkets[0] }));
      } else {
        // No markets saved. Defer the auto-open if there's an active Elite
        // upsell trigger — the banner is higher-revenue than the onboarding
        // modal and we don't want to bury it under a dialog.
        axios.get(`${API}/elite-upsell/eligibility`)
          .then((res) => {
            if (res.data?.eligible) {
              // Skip auto-open; the banner will guide the user.
              // They can still choose markets via the "Edit Markets" button.
              return;
            }
            setShowMarketModal(true);
          })
          .catch(() => setShowMarketModal(true));
      }
    }).catch(() => {
      setUserMarkets([]);
      setShowMarketModal(true);
    });

    axios.get(`${API}/locations`).then((r) => setSuggestions(r.data.suggestions || [])).catch(console.error);
    axios.get(`${API}/analysis-defaults`).then((r) => {
      if (r.data?.is_custom && r.data?.defaults) {
        setAnalysisParams(prev => ({ ...prev, ...r.data.defaults }));
        setHasCustomDefaults(true);
        hasCustomDefaultsRef.current = true;
      }
    }).catch(console.error);
    axios.get(`${API}/mortgage-rate`).then((r) => {
      if (r.data?.rate) {
        setCurrentMortgageRate(r.data);
        if (!hasCustomDefaultsRef.current) {
          setAnalysisParams(prev => ({ ...prev, interest_rate: r.data.rate }));
        }
      }
    }).catch(console.error);
    fetchSavedProperties();
    fetchAlerts();
    fetchSavedSearches();
  }, [user]);

  // Auto-search once when location is first set from saved markets (Pro users only)
  useEffect(() => {
    if (!initialAutoSearchDone.current && filters.location && subscriptionRef.current?.is_paid) {
      initialAutoSearchDone.current = true;
      searchProperties();
    }
  }, [filters.location, subscription, searchProperties]);

  // Auto-load a property from ?property=ID query param (from city deal pages or social posts)
  const [searchParams, setSearchParams] = useSearchParams();
  const autoLoadDone = useRef(false);
  const [teaserProperty, setTeaserProperty] = useState(null);
  const [teaserAnalysis, setTeaserAnalysis] = useState(null);
  const [showTeaser, setShowTeaser] = useState(false);
  
  useEffect(() => {
    let propertyId = searchParams.get("property");
    if (!propertyId || autoLoadDone.current) return;
    
    // Sanitize: old social posts have broken UTM params (double ?) baked into the property ID
    // e.g. "43824088?utm_source=facebook&utm_medium=social" — strip everything after ?
    if (propertyId.includes("?")) {
      propertyId = propertyId.split("?")[0];
    }
    // Also strip any non-numeric chars that may have leaked in
    propertyId = propertyId.replace(/[^a-zA-Z0-9_-]/g, "");
    if (!propertyId) return;
    
    autoLoadDone.current = true;

    const loadProperty = async () => {
      try {
        if (isAuthenticated) {
          // Logged in: fetch full property and open modal
          try {
            const resp = await axios.get(`${API}/properties/${propertyId}`);
            const property = resp.data;
            if (property) {
              searchParams.delete("property");
              setSearchParams(searchParams, { replace: true });
              handlePropertyClick(property);
              return;
            }
          } catch (fetchErr) {
            // Property not in cache — try teaser fallback to get address, then search
            console.warn("Property not in cache, trying teaser fallback...");
          }
          // Fallback: get teaser (public) and search by address
          try {
            const teaserResp = await axios.get(`${API}/properties/${propertyId}/teaser`);
            const teaser = teaserResp.data;
            if (teaser && teaser.address) {
              const location = teaser.city && teaser.state 
                ? `${teaser.address}, ${teaser.city}, ${teaser.state}` 
                : teaser.address;
              setFilters(prev => ({ ...prev, location }));
              searchParams.delete("property");
              setSearchParams(searchParams, { replace: true });
              // Trigger search with the address
              setTimeout(() => searchProperties(1), 300);
              return;
            }
          } catch (teaserErr) {
            console.warn("Teaser fallback also failed");
          }
        } else {
          // Not logged in: redirect to public deal page for better UX
          // This ensures social media links work without requiring login
          navigate(`/deal/${propertyId}`, { replace: true });
          return;
        }
        // If all lookups failed, just clear the param
        searchParams.delete("property");
        setSearchParams(searchParams, { replace: true });
      } catch (err) {
        console.error("Failed to auto-load property:", err);
        searchParams.delete("property");
        setSearchParams(searchParams, { replace: true });
      }
    };
    loadProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams, isAuthenticated, navigate]);

  // Auto-search for anonymous users arriving from landing page with ?search= param
  const anonSearchDone = useRef(false);
  useEffect(() => {
    if (anonSearchDone.current || user) return;
    const searchParam = searchParams.get("search");
    if (searchParam && filters.location === searchParam) {
      // Location is already set — trigger the search now
      anonSearchDone.current = true;
      searchProperties();
    } else if (searchParam && !filters.location) {
      // Set the location — next render will trigger the branch above
      setFilters(f => ({ ...f, location: searchParam }));
    }
  }, [searchParams, user, searchProperties, filters.location]);

  // Resume-token handler — a lead-magnet email links back to
  // https://realixdata.com/?resume=<token>, which we exchange for the
  // original property + params + reopens the exact same analysis modal.
  // This is what makes the transactional email actionable and gives us a
  // natural surface for the $7 tripwire CTA in Phase 2A.
  const resumeDone = useRef(false);
  useEffect(() => {
    const token = searchParams.get("resume");
    if (!token || resumeDone.current) return;
    resumeDone.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/leads/resume/${encodeURIComponent(token)}`);
        // Clean the URL so refreshes don't loop / double-open the modal.
        searchParams.delete("resume");
        setSearchParams(searchParams, { replace: true });
        if (data?.property_id) {
          const propResp = await axios
            .get(`${API}/properties/${data.property_id}`)
            .catch(() => null);
          const property = propResp?.data;
          if (property) {
            if (data.analysis_params) setAnalysisParams((p) => ({ ...p, ...data.analysis_params }));
            await handlePropertyClick(property);
            return;
          }
          navigate(`/deal/${data.property_id}`, { replace: true });
        }
      } catch (err) {
        // Expired/invalid tokens fail silently — the user still sees the
        // dashboard, they're just not auto-navigated.
        console.warn("Resume token could not be resolved:", err?.response?.status);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    // Only auto-re-search on filter changes if the user has already manually searched
    if (!hasSearched) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchProperties(), 1500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [
    filters.property_types,
    filters.min_price,
    filters.max_price,
    filters.min_beds,
    filters.min_baths,
    filters.listed_within_days,
    smartSort,
    listingStatuses,
    hasSearched,
    searchProperties,
  ]);

  const fetchSavedProperties = async () => {
    try {
      const response = await axios.get(`${API}/saved-properties`);
      const savedData = Array.isArray(response.data) ? response.data : [];
      setSavedProperties(savedData);
      setSavedPropertyIds(new Set(savedData.map((s) => s?.property?.id).filter(Boolean)));
    } catch (error) {
      console.error("Error fetching saved:", error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${API}/alerts`);
      setAlerts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  };

  const fetchSavedSearches = async () => {
    try {
      const response = await axios.get(`${API}/saved-searches`);
      setSavedSearches(response.data.searches || []);
    } catch (error) {
      console.error("Error fetching saved searches:", error);
    }
  };

  const handleSaveSearch = async () => {
    const name = prompt("Name this saved search:", filters.location || "My Search");
    if (!name) return;
    try {
      await axios.post(`${API}/saved-searches`, {
        name,
        filters,
        investment_filters: investmentFilters,
      });
      toast.success("Search saved!");
      fetchSavedSearches();
    } catch (error) {
      toast.error("Failed to save search");
    }
  };

  const handleLoadSearch = async (search) => {
    setFilters(search.filters || {});
    setInvestmentFilters(prev => ({
      ...prev,
      ...(search.investment_filters || {}),
    }));
    toast.success(`Loaded "${search.name}"`);
    // Mark as used
    axios.put(`${API}/saved-searches/${search.id}/use`).catch(() => {});
    // Trigger search after a tick to let state update
    setTimeout(() => searchProperties(), 100);
  };

  const handleDeleteSearch = async (searchId) => {
    try {
      await axios.delete(`${API}/saved-searches/${searchId}`);
      setSavedSearches(prev => prev.filter(s => s.id !== searchId));
      toast.success("Saved search deleted");
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handlePropertyClick = async (property) => {
    setSelectedProperty(property);
    trackAnalysisView(property.id);
    trackPropertyClick(property.id, property.deal_score, property.price);
    // Funnel: fire top-of-funnel event immediately. The `analysis_id`
    // doesn't exist yet — `analysis_started` uses a "pending" sentinel and
    // gets correlated to the session once `analysis_completed` fires (below,
    // via registerAnalysis).
    trackFunnelEvent("analysis_started", { property_id: property.id });
    if (property.is_seized) {
      setSelectedAnalysis(null);
      setIsModalOpen(true);
      return;
    }
    setLoading(true);
    try {
      // Load saved params for this property if they exist. The active
      // strategy rides along so the backend returns Strategy Match +
      // strategy-weighted score (additive fields, cache-safe).
      const strategyParam = activeStrategyId && activeStrategyId !== 'balanced' ? { strategy_id: activeStrategyId } : {};
      const analysisPromise = axios.get(`${API}/properties/${property.id}/analysis`, { params: { ...analysisParams, ...strategyParam } });
      const paramsPromise = isAuthenticated
        ? axios.get(`${API}/saved-analysis-params/${property.id}`).catch(() => ({ data: { params: null } }))
        : Promise.resolve({ data: { params: null } });
      const [analysisResponse, paramsResponse] = await Promise.all([analysisPromise, paramsPromise]);

      const savedParams = paramsResponse.data?.params;
      let finalAnalysis;
      let finalParams;
      if (savedParams) {
        const merged = { ...analysisParams, ...savedParams };
        setAnalysisParams(merged);
        // Re-fetch analysis with saved params
        const reAnalysis = await axios.get(`${API}/properties/${property.id}/analysis`, { params: { ...merged, ...strategyParam } });
        setSelectedAnalysis(reAnalysis.data);
        setAnalyses((prev) => ({ ...prev, [property.id]: reAnalysis.data }));
        finalAnalysis = reAnalysis.data;
        finalParams = merged;
      } else {
        setSelectedAnalysis(analysisResponse.data);
        setAnalyses((prev) => ({ ...prev, [property.id]: analysisResponse.data }));
        finalAnalysis = analysisResponse.data;
        finalParams = analysisParams;
      }

      setIsModalOpen(true);
      // Funnel: register the anonymous analysis session with the SUMMARY
      // metrics (not the full payload — keeps the doc small). This also
      // fires the `analysis_completed` event server-side so the funnel
      // stays consistent even if the browser tab is closed right after.
      registerAnalysis({
        property_id: property.id,
        property_snapshot: {
          address: property.address,
          price: property.price,
          beds: property.beds,
          baths: property.baths,
          city: property.city,
          state: property.state,
          zip: property.zip,
        },
        analysis_params: finalParams,
        analysis_result_summary: {
          deal_score: finalAnalysis?.deal_score,
          monthly_cash_flow: finalAnalysis?.monthly_cash_flow,
          cap_rate: finalAnalysis?.cap_rate,
          cash_on_cash: finalAnalysis?.cash_on_cash_return,
        },
      });
      axios
        .get(`${API}/subscription`)
        .then((r) => setSubscription(r.data))
        .catch(() => {});
    } catch (error) {
      if (error.response?.status === 401) {
        setIsAuthGateOpen(true);
      } else if (error.response?.status === 429) {
        const detail = error.response?.data?.detail;
        // Anonymous analysis cap → surface the auth gate, not the upgrade modal.
        if (detail?.anonymous_limit) {
          setIsAuthGateOpen(true);
        } else {
          const msg = typeof detail === 'object' ? detail.message : detail;
          toast.error(msg || "Analysis limit reached");
          setUpgradeReason("analysis");
          setIsUpgradeOpen(true);
        }
      } else {
        toast.error("Failed to load analysis");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleParamsChange = useCallback(
    async (newParams) => {
      setAnalysisParams(newParams);
      if (selectedProperty) {
        try {
          const response = await axios.get(
            `${API}/properties/${selectedProperty.id}/analysis`,
            { params: newParams }
          );
          setSelectedAnalysis(response.data);
        } catch (error) {
          console.error("Error:", error);
        }
      }
    },
    [selectedProperty]
  );

  const handleSaveParams = useCallback(
    async () => {
      if (!selectedProperty) return;
      try {
        await axios.put(`${API}/saved-analysis-params/${selectedProperty.id}`, analysisParams);
        toast.success("Analysis settings saved for this property");
      } catch {
        toast.error("Failed to save settings");
      }
    },
    [selectedProperty, analysisParams]
  );

  const handleSaveToggle = async (propertyId) => {
    try {
      if (savedPropertyIds.has(propertyId)) {
        await axios.delete(`${API}/saved-properties/${propertyId}`);
        setSavedPropertyIds((prev) => {
          const n = new Set(prev);
          n.delete(propertyId);
          return n;
        });
        toast.success("Property removed from saved");
      } else {
        trackSaveProperty(propertyId);
        await axios.post(`${API}/saved-properties/${propertyId}`, null);
        setSavedPropertyIds((prev) => new Set(prev).add(propertyId));
        toast.success("Property saved!");
      }
      fetchSavedProperties();
    } catch {
      toast.error("Failed to update saved properties");
    }
  };

  const handleCreateAlert = async (alertData) => {
    try {
      await axios.post(`${API}/alerts`, alertData);
      toast.success("Alert created!");
      fetchAlerts();
    } catch (error) {
      toast.error("Failed to create alert");
      throw error;
    }
  };

  const handleCheckAlert = async (alertId) => {
    try {
      toast.loading("Searching Zillow for matching properties...", {
        id: "alert-check",
      });
      const response = await axios.post(`${API}/alerts/${alertId}/check`, {});
      const { matches_count, notifications_sent } = response.data;
      toast.dismiss("alert-check");
      if (matches_count > 0) {
        const emailSent = notifications_sent.find(
          (n) => n.type === "email" && n.status === "sent"
        );
        toast.success(
          emailSent
            ? `Found ${matches_count} matches! Email sent to ${emailSent.recipient}`
            : `Found ${matches_count} matching properties!`
        );
      } else {
        toast.info("No matching properties found for this alert");
      }
      fetchAlerts();
    } catch {
      toast.dismiss("alert-check");
      toast.error("Failed to check alert");
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`);
      toast.success("Alert deleted");
      fetchAlerts();
    } catch {
      toast.error("Failed to delete alert");
    }
  };

  // Stats computed from FILTERED properties (not all)
  const profitableCount = filteredProperties.filter(
    (p) => analyses[p.id]?.is_income_producing
  ).length;

  const totalAnalyzedFiltered = properties.filter(p => analyses[p.id]).length;

  const avgDealScore = useMemo(() => {
    const scores = filteredProperties
      .map((p) => analyses[p.id]?.deal_score)
      .filter((s) => s != null);
    return scores.length > 0
      ? Math.round(scores.reduce((s, v) => s + v, 0) / scores.length)
      : 0;
  }, [analyses, filteredProperties]);

  const avgCashFlow = useMemo(() => {
    const cfs = filteredProperties
      .map((p) => analyses[p.id]?.cash_flow_monthly)
      .filter((c) => c != null);
    return cfs.length > 0
      ? Math.round(cfs.reduce((s, v) => s + v, 0) / cfs.length)
      : 0;
  }, [analyses, filteredProperties]);

  const handleOnboardingComplete = (prefs) => {
    localStorage.setItem("relixdata_onboarded", "true");
    trackOnboardingComplete(prefs);
    if (prefs.locations.length > 0)
      setFilters((f) => ({ ...f, location: prefs.locations[0] }));
    if (prefs.budgetMin)
      setFilters((f) => ({ ...f, minPrice: prefs.budgetMin }));
    if (prefs.budgetMax)
      setFilters((f) => ({ ...f, maxPrice: prefs.budgetMax }));
    if (prefs.strategies.includes("cash_flow"))
      setInvestmentFilters((f) => ({ ...f, onlyPositiveCashFlow: true }));
    if (prefs.strategies.includes("multi_family"))
      setMultiSubTypes((s) => ({
        ...s,
        duplex: true,
        triplex: true,
        quadplex: true,
      }));
    if (prefs.locations.length > 0)
      setTimeout(() => searchProperties(), 300);
    setShowOnboarding(false);
  };

  const handleSavedPropertyClick = (saved) => {
    setSelectedProperty(saved.property);
    axios
      .get(`${API}/properties/${saved.property.id}/analysis`, {
        params: analysisParams,
      })
      .then((r) => {
        setSelectedAnalysis(r.data);
        setIsModalOpen(true);
      });
  };

  const handleCompare = (items) => {
    setComparisonItems(items);
    setIsComparisonOpen(true);
  };

  const handleMarketsSelected = (selectedMarkets) => {
    setUserMarkets(selectedMarkets);
    setShowMarketModal(false);
    if (selectedMarkets.length > 0) {
      setFilters((f) => ({ ...f, location: selectedMarkets[0] }));
      // Auto-search for Pro users only
      if (subscriptionRef.current?.is_paid) {
        setTimeout(() => searchProperties(), 300);
      }
    }
  };

  const handleChangeMarkets = () => {
    setShowMarketModal(true);
  };

  const handleSaveDefaults = async (newDefaults) => {
    try {
      await axios.put(`${API}/analysis-defaults`, newDefaults);
      setAnalysisParams(prev => ({ ...prev, ...newDefaults }));
      setHasCustomDefaults(true);
      hasCustomDefaultsRef.current = true;
      setShowDefaultsEditor(false);
      toast.success("Analysis defaults saved — all new analyses will use your numbers");
    } catch {
      toast.error("Failed to save defaults");
    }
  };

  const handleResetDefaults = async () => {
    try {
      const r = await axios.delete(`${API}/analysis-defaults`);
      setAnalysisParams(prev => ({ ...prev, ...r.data.defaults }));
      setHasCustomDefaults(false);
      hasCustomDefaultsRef.current = false;
      setShowDefaultsEditor(false);
      toast.success("Reset to platform defaults");
    } catch {
      toast.error("Failed to reset defaults");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader
        user={user}
        isAuthenticated={isAuthenticated}
        logout={logout}
        properties={properties}
        totalCount={totalCount}
        weeklyDigest={weeklyDigest}
        toggleDigest={toggleDigest}
        currentMortgageRate={currentMortgageRate}
        subscription={subscription}
      />

      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-4">
        {/* My Analysis Defaults — top of dashboard */}
        <div className="mb-4 hidden sm:block" data-testid="my-defaults-section">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              {!showDefaultsEditor ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <SlidersHorizontal className="w-4 h-4 text-emerald-500" />
                    <div>
                      <p className="text-sm font-medium">My Analysis Defaults</p>
                      <p className="text-xs text-muted-foreground">
                        {hasCustomDefaults ? (
                          <span className="text-emerald-500">Custom: {analysisParams.down_payment_percent}% down · {analysisParams.interest_rate}% rate · {analysisParams.loan_term_years}yr · ${Number(analysisParams.repair_costs).toLocaleString()} repairs</span>
                        ) : (
                          "Using platform defaults — set your own numbers"
                        )}
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowDefaultsEditor(true)} className="text-xs h-7" data-testid="edit-defaults-btn">
                    <Pencil className="w-3 h-3 mr-1" /> Edit
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4 text-emerald-500" />
                      <p className="text-sm font-semibold">Set Your Analysis Defaults</p>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Applied to all property analyses</p>
                  </div>
                  <AnalysisDefaultsEditor
                    defaults={analysisParams}
                    onSave={handleSaveDefaults}
                    onReset={hasCustomDefaults ? handleResetDefaults : null}
                    onCancel={() => setShowDefaultsEditor(false)}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <EliteUpsellBanner className="mb-4" />

        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);        }}>
          <TabsList className="grid w-full max-w-3xl grid-cols-5">
            <TabsTrigger
              value="search"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="tab-search"
              onClick={() => {
                if (activeTab === "search") {
                  setMobileFiltersOpen(prev => !prev);
                }
              }}
            >
              <Search className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Search</span>
            </TabsTrigger>
            <TabsTrigger
              value="compare"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="tab-compare"
            >
              <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Compare</span>
            </TabsTrigger>
            <TabsTrigger
              value="portfolio"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="tab-portfolio"
            >
              <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Portfolio</span>
            </TabsTrigger>
            <TabsTrigger
              value="bulk"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="tab-bulk"
            >
              <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Bulk</span>
            </TabsTrigger>
            <TabsTrigger
              value="saved"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="tab-saved"
            >
              <Heart className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Saved</span> (
              {savedPropertyIds.size})
            </TabsTrigger>
            <TabsTrigger
              value="alerts"
              className="flex items-center gap-1.5 text-xs sm:text-sm"
              data-testid="tab-alerts"
            >
              <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Alerts</span> (
              {alerts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="search" className="mt-4 sm:mt-6">
            <SearchTab
              filters={filters}
              setFilters={setFilters}
              suggestions={suggestions}
              searchProperties={searchProperties}
              loading={loading}
              smartSort={smartSort}
              setSmartSort={setSmartSort}
              investmentFilters={investmentFilters}
              setInvestmentFilters={setInvestmentFilters}
              multiSubTypes={multiSubTypes}
              setMultiSubTypes={setMultiSubTypes}
              sortBy={sortBy}
              setSortBy={setSortBy}
              listingStatuses={listingStatuses}
              setListingStatuses={setListingStatuses}
              subscription={subscription}
              properties={properties}
              filteredProperties={filteredProperties}
              paginatedProperties={paginatedProperties}
              totalPages={totalPages}
              analyses={analyses}
              analysisLoadProgress={analysisLoadProgress}
              profitableCount={profitableCount}
              avgDealScore={avgDealScore}
              avgCashFlow={avgCashFlow}
              totalAnalyzedFiltered={totalAnalyzedFiltered}
              propertiesVisible={propertiesVisible}
              handlePropertyClick={handlePropertyClick}
              savedPropertyIds={savedPropertyIds}
              handleSaveToggle={handleSaveToggle}
              setUpgradeReason={setUpgradeReason}
              setIsUpgradeOpen={setIsUpgradeOpen}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              apiPage={apiPage}
              setApiPage={setApiPage}
              totalCount={totalCount}
              mobileFiltersOpen={mobileFiltersOpen}
              setMobileFiltersOpen={setMobileFiltersOpen}
              onSaveSearch={handleSaveSearch}
              savedSearches={savedSearches}
              onLoadSearch={handleLoadSearch}
              onDeleteSearch={handleDeleteSearch}
              hasSearched={hasSearched}
              userMarkets={userMarkets}
              onChangeMarkets={handleChangeMarkets}
              strategyMode={strategyMode}
              onStrategyChange={handleStrategyChange}
              isAuthenticated={isAuthenticated}
              activeStrategyId={activeStrategyId}
              activeStrategyWeights={activeStrategyWeights}
              activeStrategyCriteria={activeStrategyCriteria}
              onActiveStrategyChange={handleActiveStrategyChange}
              onUpgradeElite={(reason) => { setUpgradeReason(reason || "elite:custom_scoring"); setIsUpgradeOpen(true); }}
              onPropertyRefreshed={(fresh) => {
                // Feb 2026 — Elite Refresh button lands here. Patch the
                // in-memory properties list AND invalidate the search
                // cache so a re-search reflects the fresh Zillow data.
                setProperties((prev) => prev.map((p) => p.id === fresh.id ? { ...p, ...fresh } : p));
                searchCacheRef.current.clear();
              }}
            />
          </TabsContent>

          <TabsContent value="compare" className="mt-4 sm:mt-6">
            <MarketCompareTab subscription={subscription} onUpgrade={(reason) => { setUpgradeReason(reason); setIsUpgradeOpen(true); }} />
          </TabsContent>

          <TabsContent value="portfolio" className="mt-4 sm:mt-6">
            <PortfolioTab />
          </TabsContent>

          <TabsContent value="bulk" className="mt-4 sm:mt-6">
            <BulkAnalyzeTab
              subscription={subscription}
              onUpgrade={(reason) => { setUpgradeReason(reason || "elite:bulk_analyze"); setIsUpgradeOpen(true); }}
            />
          </TabsContent>

          <TabsContent value="saved" className="mt-6">
            <SavedTab
              savedProperties={savedProperties}
              savedPropertyIds={savedPropertyIds}
              handleSaveToggle={handleSaveToggle}
              onPropertyClick={handleSavedPropertyClick}
              onCompare={handleCompare}
              subscription={subscription}
            />
          </TabsContent>

          <TabsContent value="alerts" className="mt-6">
            <AlertsTab
              alerts={alerts}
              onCreateAlert={() => setIsCreateAlertOpen(true)}
              onCheckAlert={handleCheckAlert}
              onDeleteAlert={handleDeleteAlert}
            />
          </TabsContent>
        </Tabs>

        {/* Dashboard Widgets — Market Alerts & Deal of the Day */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-testid="dashboard-widgets">
          <MarketAlertsWidget />
          <DealOfDayWidget />
        </div>
      </div>

      <AnalysisModal
        property={selectedProperty}
        analysis={selectedAnalysis}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        params={analysisParams}
        onParamsChange={handleParamsChange}
        isSaved={
          selectedProperty
            ? savedPropertyIds.has(selectedProperty.id)
            : false
        }
        onSaveToggle={handleSaveToggle}
        onSaveParams={handleSaveParams}
        subscription={subscription}
        onUpgrade={(reason) => { setUpgradeReason(reason); setIsUpgradeOpen(true); }}
        isAuthenticated={isAuthenticated}
      />
      <ComparisonModal
        isOpen={isComparisonOpen}
        onClose={() => setIsComparisonOpen(false)}
        selectedItems={comparisonItems}
        analysisParams={analysisParams}
      />
      <CreateAlertModal
        isOpen={isCreateAlertOpen}
        onClose={() => setIsCreateAlertOpen(false)}
        onCreateAlert={handleCreateAlert}
      />
      <UpgradeModal
        isOpen={isUpgradeOpen}
        onClose={() => setIsUpgradeOpen(false)}
        currentTier={subscription?.tier || "free"}
        limitType={upgradeReason}
      />
      <OnboardingModal
        isOpen={showOnboarding && !showMarketModal && !showTeaser}
        onClose={() => {
          localStorage.setItem("relixdata_onboarded", "true");
          setShowOnboarding(false);
        }}
        onComplete={handleOnboardingComplete}
        userName={user?.name}
      />
      <MarketSelectionModal
        isOpen={showMarketModal}
        onClose={() => setShowMarketModal(false)}
        onMarketsSelected={handleMarketsSelected}
        allowSkip={userMarkets && userMarkets.length > 0}
      />
      <FeedbackWidget user={user} />
      <SupportContact />

      {/* Auth Gate Modal for non-logged-in users */}
      {isAuthGateOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="auth-gate-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl p-6 sm:p-8 max-w-sm w-full mx-4 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-emerald-500" />
            </div>
            <h2 className="text-lg font-bold mb-2" data-testid="auth-gate-title">
              {properties.length > 0 ? "Unlock Unlimited Deal Analysis" : "Create a Free Account to Search"}
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              {properties.length > 0
                ? "You've used your free preview. Sign up to get full ROI breakdowns, expense projections, saved deals, and alerts — free forever."
                : "Sign up free to search properties, view deal analysis, and get instant cash flow projections."
              }
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => { setIsAuthGateOpen(false); navigate('/auth?mode=signup'); }}
                className="w-full py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                data-testid="auth-gate-signup-btn"
              >
                Sign Up Free — 10 Seconds
              </button>
              <button
                onClick={() => { setIsAuthGateOpen(false); navigate('/auth'); }}
                className="w-full py-2.5 rounded-lg bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors border border-border"
                data-testid="auth-gate-login-btn"
              >
                Log In
              </button>
              <button
                onClick={() => setIsAuthGateOpen(false)}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors mt-1"
                data-testid="auth-gate-close-btn"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Property Teaser Modal for non-authenticated users from social links */}
      {showTeaser && teaserProperty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="property-teaser-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Property Image */}
            {(teaserProperty.image_url || teaserProperty.imgSrc) && (
              <div className="relative h-48 sm:h-56 overflow-hidden rounded-t-xl">
                <img 
                  src={teaserProperty.image_url || teaserProperty.imgSrc} 
                  alt={teaserProperty.address}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <h2 className="text-lg sm:text-xl font-bold text-white" data-testid="teaser-address">{teaserProperty.address}</h2>
                  <p className="text-sm text-white/80">{teaserProperty.city}, {teaserProperty.state} {teaserProperty.zip_code}</p>
                </div>
              </div>
            )}
            
            <div className="p-4 sm:p-6 space-y-4">
              {/* Visible Property Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="teaser-visible-stats">
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">List Price</p>
                  <p className="text-lg font-bold text-emerald-500">${(teaserProperty.price || 0).toLocaleString()}</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Beds</p>
                  <p className="text-lg font-bold">{teaserProperty.bedrooms || '-'}</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Baths</p>
                  <p className="text-lg font-bold">{teaserProperty.bathrooms || '-'}</p>
                </div>
                <div className="bg-secondary/30 rounded-lg p-3 text-center">
                  <p className="text-xs text-muted-foreground">Sqft</p>
                  <p className="text-lg font-bold">{(teaserProperty.sqft || teaserProperty.livingArea || 0).toLocaleString() || '-'}</p>
                </div>
              </div>

              {/* Blurred Analysis Section */}
              <div className="relative" data-testid="teaser-blurred-section">
                <div className="filter blur-sm pointer-events-none select-none opacity-60">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Monthly Cash Flow</p>
                      <p className="text-lg font-bold text-emerald-500">$2,847</p>
                    </div>
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Cap Rate</p>
                      <p className="text-lg font-bold text-blue-400">12.4%</p>
                    </div>
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Deal Score</p>
                      <p className="text-lg font-bold text-amber-400">87</p>
                    </div>
                    <div className="bg-violet-500/10 border border-violet-500/20 rounded-lg p-3 text-center">
                      <p className="text-xs text-muted-foreground">Monthly Rent</p>
                      <p className="text-lg font-bold text-violet-400">$4,200</p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <div className="bg-secondary/50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-muted-foreground">Mortgage</p>
                      <p className="text-sm font-mono">$1,353/mo</p>
                    </div>
                    <div className="bg-secondary/50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-muted-foreground">CoC Return</p>
                      <p className="text-sm font-mono">18.2%</p>
                    </div>
                    <div className="bg-secondary/50 rounded-lg p-2 text-center">
                      <p className="text-[10px] text-muted-foreground">BRRRR Score</p>
                      <p className="text-sm font-mono">72</p>
                    </div>
                  </div>
                </div>
                
                {/* Overlay CTA */}
                <div className="absolute inset-0 flex items-center justify-center bg-card/40 backdrop-blur-[2px] rounded-lg">
                  <div className="text-center p-4">
                    <div className="w-10 h-10 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-2">
                      <Lock className="w-5 h-5 text-emerald-500" />
                    </div>
                    <p className="text-sm font-medium mb-1">Full Analysis Hidden</p>
                    <p className="text-xs text-muted-foreground">Sign up free to see cash flow, cap rate, and deal score</p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col gap-3 pt-2" data-testid="teaser-cta-section">
                <button
                  onClick={() => {
                    const propertyId = teaserProperty.id || teaserProperty.zpid;
                    setShowTeaser(false);
                    navigate(`/auth?mode=signup&redirect=/dashboard?property=${propertyId}`);
                  }}
                  className="w-full py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                  data-testid="teaser-signup-btn"
                >
                  Sign Up Free to View Full Analysis
                </button>
                <button
                  onClick={() => {
                    const propertyId = teaserProperty.id || teaserProperty.zpid;
                    setShowTeaser(false);
                    navigate(`/auth?redirect=/dashboard?property=${propertyId}`);
                  }}
                  className="w-full py-2.5 rounded-lg bg-secondary hover:bg-secondary/80 text-foreground text-sm font-medium transition-colors border border-border"
                  data-testid="teaser-login-btn"
                >
                  Already have an account? Log In
                </button>
                <button
                  onClick={() => {
                    setShowTeaser(false);
                    searchParams.delete("property");
                    setSearchParams(searchParams, { replace: true });
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="teaser-close-btn"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
